/**
 * Auth Repository
 * Handles all authentication-related API calls
 */

import { apiClient } from '../client'
import { Tenant } from './tenant.repository'

export interface LoginRequest {
  username: string
  password: string
}

export interface LoginResponse {
  message: string
  data: {
    access_token: string
    user: {
      id: number
      username: string
      en_name: string
      ar_name?: string
      email?: string
      role_id: number
      report_to: number
      tenants: Tenant[]
      permissions?: Record<string, string[]>
    }
  }
}

export interface UserProfile {
  id: number
  username: string
  en_name: string
  ar_name?: string
  email?: string
  role_id: number
  report_to: number
  tenants: Tenant[]
  permissions?: Record<string, string[]>
}

export interface VerifyTokenResponse {
  valid: boolean
  message: string
}

class AuthRepository {
  /**
   * Login user
   */
  async login(credentials: LoginRequest): Promise<LoginResponse> {
    return apiClient.post<LoginResponse>('/auth/login', credentials, false)
  }

  /**
   * Logout user
   */
  async logout(): Promise<{ message: string }> {
    // Send empty body to ensure proper request formatting
    return apiClient.post<{ message: string }>('/auth/logout', {})
  }

  /**
   * Get current user profile
   */
  async getProfile(): Promise<{ user: UserProfile }> {
    return apiClient.get<{ user: UserProfile }>('/auth/me')
  }

  /**
   * Verify token validity
   */
  async verifyToken(): Promise<VerifyTokenResponse> {
    return apiClient.get<VerifyTokenResponse>('/auth/verify')
  }

  /**
   * Change password for the logged-in user (requires current password).
   */
  async changePassword(currentPassword: string, newPassword: string): Promise<{ success: boolean; message: string }> {
    return apiClient.post<{ success: boolean; message: string }>(
      '/auth/change-password',
      { current_password: currentPassword, new_password: newPassword },
      true,
    )
  }
}

// Export singleton instance
export const authRepository = new AuthRepository()

